import React, { useRef, useState, useEffect } from "react";

interface VideoPlayerProps {
  videoUrl: string;
  posterUrl: string;
  title: string;
  autoplay?: boolean;
}

export function VideoPlayer({
  videoUrl,
  posterUrl,
  title,
  autoplay = false,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showPlayButton, setShowPlayButton] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      // Video metadata loaded successfully
    };

    const handleError = (e: Event) => {
      console.error("Video error:", e);
      setHasError(true);
      setShowPlayButton(true);
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("error", handleError);

    if (autoplay) {
      // Attempt autoplay (must be muted)
      video.muted = true;
      video
        .play()
        .then(() => {
          setIsPlaying(true);
          setShowPlayButton(false);
        })
        .catch(() => {
          // Autoplay failed, show play button
          setShowPlayButton(true);
        });
    }

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("error", handleError);
    };
  }, [autoplay]);

  const handlePlayClick = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
      setIsPlaying(false);
    } else {
      video.muted = true; // Ensure muted for first play
      video.play().then(() => {
        setIsPlaying(true);
        setShowPlayButton(false);
      });
    }
  };

  const handleMuteToggle = () => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  return (
    <div className="relative w-full aspect-auto md:aspect-video md:max-h-[65vh] rounded-lg overflow-hidden group bg-black">
      {/* Video with constrained height */}
      <video
        ref={videoRef}
        className={`w-full h-auto md:h-full object-contain ${hasError ? "hidden" : ""}`}
        poster={posterUrl}
        loop
        playsInline
        preload="metadata"
        aria-label={title}
      >
        <source src={videoUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Fallback poster image for errors */}
      {hasError && (
        <img
          src={posterUrl}
          alt={title}
          className="w-full h-auto md:h-full object-contain"
        />
      )}

      {/* Play Button Overlay */}
      {showPlayButton && (
        <button
          onClick={handlePlayClick}
          className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 hover:bg-opacity-40 transition-all focus:outline-none"
          aria-label={isPlaying ? "Pause video" : "Play video"}
        >
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-blue-600 rounded-full flex items-center justify-center shadow-2xl hover:scale-110 hover:bg-blue-700 transition-all">
            {isPlaying ? (
              <svg
                className="w-8 h-8 sm:w-10 sm:h-10 text-white"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
            ) : (
              <svg
                className="w-8 h-8 sm:w-10 sm:h-10 ml-1 text-white"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </div>
        </button>
      )}

      {/* Unmute Button (Near video center-right) */}
      {isPlaying && (
        <button
          onClick={handleMuteToggle}
          className="absolute bottom-4 left-4 w-11 h-11 bg-black bg-opacity-60 hover:bg-opacity-80 rounded-full flex items-center justify-center transition-all focus:outline-none focus:ring-2 focus:ring-white"
          aria-label={isMuted ? "Unmute video" : "Mute video"}
        >
          {isMuted ? (
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              <path d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
            </svg>
          ) : (
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
            </svg>
          )}
        </button>
      )}
    </div>
  );
}
